// assets/product-engraving.js
import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductEngravingRefs
 * @property {HTMLInputElement} engravingCheckbox - The "Add engraving" opt-in checkbox.
 * @property {HTMLButtonElement} toggleButton - The "Add engraving - optional" link/button.
 * @property {HTMLElement} panel - The expandable engraving panel.
 * @property {HTMLElement} previewText - The live text preview.
 * @property {HTMLInputElement} textInput - The engraving text input.
 * @property {HTMLInputElement} styleInput - Hidden input carrying the selected font name.
 * @property {HTMLElement} minLengthError - "Min X letters" error message.
 * @property {HTMLElement} maxLengthError - "Max X letters" error message.
 * @property {HTMLElement} invalidCharsError - Invalid-characters error message.
 * @property {HTMLElement} emptyTextError - "Please enter your engraving text" error message.
 * @property {HTMLInputElement} disclaimerCheckbox - The mandatory disclaimer checkbox.
 * @property {HTMLElement} disclaimerError - "Please accept the disclaimer" error message.
 * @property {HTMLElement} [deliveryMessage] - The updated lead-time delivery message.
 * @property {HTMLElement} previewPanel - The engraving preview panel (metal background + text).
 *
 * A custom element that manages the PDP engraving accordion: expand/collapse,
 * font selection, text validation, lead-time recalculation, and Add to Bag CTA swap.
 * @extends Component<ProductEngravingRefs>
 */
class ProductEngravingComponent extends Component {
  // Intentionally no requiredRefs: the theme editor's block-reorder preview
  // can render this block in isolation without the full page context —
  // every handler below already guards optional refs, so we degrade
  // quietly there instead of throwing MissingRefError. (The other cause of
  // a duplicate/empty instance — this schema declaring "tag" while also
  // manually authoring the <product-engraving-component> tag in the
  // Liquid body — is fixed by setting "tag": null in the schema.)

  /** @type {string | null} */
  #originalEstimate = null;
  /** @type {string | null} */
  #originalExpectedShip = null;
  /** @type {string | null} */
  #originalEngravingLeadTime = null;
  #engravingLeadTimeInputCreated = false;
  /** @type {Map<Element, string>} */
  #originalPlaceholderHtml = new Map();
  #cachedOriginals = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #lengthValidationTimer = undefined;
  /** @type {string | null} */
  #ocBase = null;
  /** @type {string | null} */
  #ocApplied = null;
  /** @type {Record<string, {hex: string, textHex: string, image: string, plain?: boolean}>} */
  #metalColorMap = {};

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // `invalid` does not bubble, so a capture-phase listener on the component
    // is the only way to catch it without holding a direct ref (which may not
    // exist yet at connect time).
    this.addEventListener('invalid', this.#handleInvalid, true);
    // Capture on `window`, not `document`: the theme's declarative event
    // framework delegates on `document` in the capture phase and serializes
    // the form synchronously inside its submit handler — window-capture is
    // the only spot guaranteed to run before that, so the engraving cart
    // properties below are in place when the FormData is built.
    window.addEventListener('submit', this.#handleFormSubmit, true);

    try {
      this.#metalColorMap = JSON.parse(this.dataset.metalColorMap || '{}');
    } catch {
      this.#metalColorMap = {};
    }
    this.#variantUpdateSection = this.closest('.shopify-section');
    this.#variantUpdateSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#lengthValidationTimer);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('invalid', this.#handleInvalid, true);
    window.removeEventListener('submit', this.#handleFormSubmit, true);
    this.#variantUpdateSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /** @type {Element | null} */
  #variantUpdateSection = null;

  /**
   * Handles variant update events: swaps the preview background/text color
   * for the newly selected variant's metal color, and re-applies the "Add
   * custom item to bag" CTA label if engraving is active — the add-to-cart
   * button text gets reset by the variant morph otherwise, since the server
   * re-render has no idea engraving is turned on client-side.
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    const available = event.detail?.resource?.available !== false;
    this.#setAvailability(available);

    if (variantId != null) {
      const metalColor = this.#metalColorMap[String(variantId)];
      const { previewPanel } = this.refs;

      if (metalColor && previewPanel) {
        previewPanel.style.setProperty('--engraving-metal', metalColor.hex);
        previewPanel.style.setProperty('--engraving-text', metalColor.textHex);

        if (metalColor.plain) {
          previewPanel.style.background = '#FFFFFF';
          previewPanel.classList.remove('product-engraving__preview--image');
        } else if (metalColor.image) {
          previewPanel.style.background = '';
          previewPanel.style.backgroundImage = `url('${metalColor.image}')`;
          previewPanel.classList.add('product-engraving__preview--image');
        } else {
          previewPanel.style.background = '';
          previewPanel.style.backgroundImage = '';
          previewPanel.classList.remove('product-engraving__preview--image');
        }
      }
    }

    // Deferred so it runs after every other `variant:update` listener
    // (including whatever resets the add-to-cart button text) has finished
    // for this dispatch, regardless of listener registration order.
    if (this.dataset.active === 'true') {
      queueMicrotask(() => this.#updateCtaText(true));
    }
  };

  // Re-sync just before the form is serialized: `_oc` is written into the
  // form asynchronously (cause.js → Netlify), so it may not have existed yet
  // when the checkbox was ticked, or may have been rewritten since.
  /** @param {Event} event */
  #handleFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== this.dataset.productFormId) return;
    if (this.dataset.active !== 'true') return;

    // Backstop for the native `required` check: submitting via Enter inside
    // the text field fires the form's implicit submission without blurring
    // it first, so a spaces-only value (still non-empty pre-trim) can sail
    // past constraint validation. Trim here before anything else runs, so a
    // spaces-only value collapses to empty and is caught below like any
    // other blank field.
    const { textInput, emptyTextError } = this.refs;
    if (textInput) {
      this.#trimTextInput();
      if (textInput.value === '') {
        event.preventDefault();
        event.stopPropagation();
        if (emptyTextError) emptyTextError.hidden = false;
        const { minLengthError, maxLengthError, invalidCharsError } = this.refs;
        if (minLengthError) minLengthError.hidden = true;
        if (maxLengthError) maxLengthError.hidden = true;
        if (invalidCharsError) invalidCharsError.hidden = true;
        textInput.focus();
        return;
      }
    }

    // Backstop for the customValidity gate (#syncLengthValidity): if a
    // submit path skips constraint validation, block the out-of-range value
    // here before the theme's document-capture submit handler serializes
    // the form.
    if (!this.#validateTextLength()) {
      event.preventDefault();
      event.stopPropagation();
      this.refs.textInput?.focus();
      return;
    }
    this.#applyEngravingCartProperties(form);
  };

  // Shows/hides the min-length error for the current value and reports
  // whether the value is within range. The max error's visibility is owned
  // by handleTextInput (it fires on the over-limit *attempt*, and the value
  // is truncated to the max right there, so length can never exceed it
  // here — checking it would just hide that message). An empty value is
  // left to the `required` constraint (#handleInvalid) rather than flagged
  // as too short.
  /** @returns {boolean} */
  #validateTextLength() {
    const { textInput, minLengthError } = this.refs;
    if (!textInput) return true;

    const length = textInput.value.length;
    const min = parseInt(textInput.dataset.minChars || '', 10) || 0;
    const max = parseInt(textInput.dataset.maxChars || '', 10) || Infinity;
    const tooShort = length > 0 && length < min;

    if (minLengthError) minLengthError.hidden = !tooShort;
    if (tooShort) {
      // Only one message at a time — the min message takes the slot from
      // any attempt message still visible from earlier typing.
      const { maxLengthError, invalidCharsError } = this.refs;
      if (maxLengthError) maxLengthError.hidden = true;
      if (invalidCharsError) invalidCharsError.hidden = true;
    }
    return !tooShort && length <= max;
  }

  /** @returns {HTMLFormElement | null} */
  #getProductForm() {
    const formId = this.dataset.productFormId;
    const form = formId ? document.getElementById(formId) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  // Adds the engraving-only cart-line properties (visible in the form while
  // engraving is active): `_engraving_olivela_cost`, `_LPROP`, and an `_oc`
  // bumped by the engraving cost. Idempotent — runs on activation and again
  // at submit, and the `#ocApplied` bookkeeping guarantees the cost is added
  // to the base value exactly once.
  /** @param {HTMLFormElement} form */
  #applyEngravingCartProperties(form) {
    const cost = parseFloat(this.dataset.engravingCost || '') || 0;

    /**
     * @param {string} name
     * @param {string} value
     */
    const upsert = (name, value) => {
      let input = /** @type {HTMLInputElement | null} */ (form.querySelector(`[name="properties[${name}]"]`));
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        form.appendChild(input);
      }
      input.value = value;
    };

    upsert('_engraving_olivela_cost', String(cost));
    upsert('_LPROP', 'Final sale');

    const ocInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="properties[_oc]"]'));
    if (ocInput && ocInput.value !== '') {
      // If another script replaced _oc since our last bump (e.g. a variant
      // change recomputed it), treat the current value as the new base so the
      // engraving cost is never added twice.
      if (this.#ocBase === null || ocInput.value !== this.#ocApplied) {
        this.#ocBase = ocInput.value;
      }
      const applied = String((parseFloat(this.#ocBase) || 0) + cost);
      ocInput.value = applied;
      this.#ocApplied = applied;
    }
  }

  #removeEngravingCartProperties() {
    const form = this.#getProductForm();

    if (form) {
      form
        .querySelectorAll('[name="properties[_engraving_olivela_cost]"], [name="properties[_LPROP]"]')
        .forEach((input) => input.remove());

      const ocInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="properties[_oc]"]'));
      // Only restore if nothing else rewrote _oc since we bumped it.
      if (ocInput && this.#ocApplied !== null && ocInput.value === this.#ocApplied) {
        ocInput.value = this.#ocBase ?? '';
      }
    }

    this.#ocBase = null;
    this.#ocApplied = null;
  }

  // The text input and disclaimer checkbox are `required` while engraving is
  // active (they are `disabled` otherwise, which exempts them from constraint
  // validation), so an attempted add-to-cart with the engraving checkbox
  // ticked but no text entered / disclaimer unticked is blocked by the
  // browser before the form ever submits. This swaps the native validation
  // bubble for the themed inline errors.
  /** @param {Event} event */
  #handleInvalid = (event) => {
    const { textInput, emptyTextError, disclaimerCheckbox, disclaimerError } = this.refs;

    if (disclaimerCheckbox && event.target === disclaimerCheckbox) {
      if (!disclaimerCheckbox.validity.valueMissing) return;
      event.preventDefault();
      if (disclaimerError) disclaimerError.hidden = false;
      // `invalid` fires in document order, so the text input (above the
      // checkbox) has already claimed focus if it is also empty — only move
      // focus here when the disclaimer is the sole blocker.
      if (!textInput || textInput.validity.valid) disclaimerCheckbox.focus();
      return;
    }

    if (!textInput || event.target !== textInput) return;
    // customError is the JS min-length gate set by #syncLengthValidity —
    // swap the native bubble for the themed "Min X letters" message.
    if (textInput.validity.customError) {
      event.preventDefault();
      this.#validateTextLength();
      textInput.focus();
      return;
    }
    if (!textInput.validity.valueMissing) return;
    event.preventDefault();
    if (emptyTextError) emptyTextError.hidden = false;
    // Single-message rule: an invalid-chars message can still be up here
    // (typing only disallowed characters leaves the value empty).
    const { minLengthError, maxLengthError, invalidCharsError } = this.refs;
    if (minLengthError) minLengthError.hidden = true;
    if (maxLengthError) maxLengthError.hidden = true;
    if (invalidCharsError) invalidCharsError.hidden = true;
    textInput.focus();
  };

  // Wired via the checkbox snippet's `events` param — hides the disclaimer
  // error as soon as the customer ticks the box.
  handleDisclaimerChange() {
    const { disclaimerCheckbox, disclaimerError } = this.refs;
    if (disclaimerError && disclaimerCheckbox?.checked) disclaimerError.hidden = true;
  }

  /** @param {Event} event */
  #handleCartAdd = (event) => {
    const cartAddEvent = /** @type {CartAddEvent} */ (event);
    // Only react to a successful add from this product's own form, and only
    // if engraving was actually part of what got added — otherwise leave
    // whatever the customer was doing untouched. CartAddEvent's `sourceId`
    // is actually the variant id (not a form/section id), so match on
    // productId instead — the one identifier the dispatching form reliably
    // carries for "which product was this."
    if (cartAddEvent.detail?.data?.didError) return;
    const productId = cartAddEvent.detail?.data?.productId;
    if (!productId || String(productId) !== this.dataset.productId) return;
    if (this.dataset.active !== 'true') return;

    if (this.refs.engravingCheckbox) this.refs.engravingCheckbox.checked = false;
    this.#setOpen(false);
    this.#clearInputs();
    this.#applyState(false);
  };

  handleToggleClick() {
    // Delegate to the checkbox's own native click so there is a single
    // code path that ever flips `checked` — clicking fires the `change`
    // event, which is wired declaratively to /handleCheckboxChange.
    this.refs.engravingCheckbox?.click();
  }

  // Wired via `on:change="/handleCheckboxChange"` on the checkbox in the
  // Liquid template — the theme's declarative binding is more reliable than
  // attaching a listener in connectedCallback (refs may not be ready yet).
  // Unchecking just collapses the accordion — it does not clear the typed
  // text/style, so re-checking later restores what was there before. Only
  // the explicit "Remove engraving" button clears the entered data.
  handleCheckboxChange() {
    const active = this.refs.engravingCheckbox?.checked ?? false;
    this.#setOpen(active);

    if (active) this.#pushGaEvent();

    this.#applyState(active);
  }

  /** @param {MouseEvent} event */
  handleStyleClick(event) {
    // event.currentTarget is not usable here — the theme's declarative
    // event framework attaches its real listener on `document`, so
    // currentTarget always reflects that, not the clicked button.
    // event.target is the one the framework correctly resolves to the
    // matched [on:click] element.
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('.product-engraving__style-option');
    if (button instanceof HTMLElement) this.#selectStyle(button);
  }

  /** @param {HTMLElement} button */
  #selectStyle(button) {
    const family = button.dataset.fontFamily || '';

    /** @type {NodeListOf<HTMLElement>} */
    (this.querySelectorAll('.product-engraving__style-option')).forEach((option) => {
      const isSelected = option === button;
      option.dataset.selected = isSelected ? 'true' : 'false';
      option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const { previewText, styleInput } = this.refs;
    if (previewText) previewText.style.fontFamily = `'${family}', serif`;
    if (styleInput) styleInput.value = button.textContent?.trim() || '';
  }

  handleTextInput() {
    const { textInput, previewText, minLengthError, maxLengthError, emptyTextError, invalidCharsError } = this.refs;
    if (!textInput) return;

    // Strip disallowed characters immediately so they never persist in the
    // input or the preview.
    const invalidAttempt = /[^a-zA-Z0-9\s]/.test(textInput.value);
    if (invalidAttempt) {
      const cursor = textInput.selectionStart ?? textInput.value.length;
      textInput.value = textInput.value.replace(/[^a-zA-Z0-9\s]/g, '');
      const newCursor = Math.max(0, cursor - 1);
      textInput.setSelectionRange(newCursor, newCursor);
    }

    // Hard-cap the value at the max — typing and paste both land here,
    // mirroring what native maxlength used to block.
    const max = parseInt(textInput.dataset.maxChars || '', 10) || Infinity;
    const maxAttempt = textInput.value.length > max;
    if (maxAttempt) {
      const cursor = textInput.selectionStart ?? max;
      textInput.value = textInput.value.slice(0, max);
      const newCursor = Math.min(cursor, max);
      textInput.setSelectionRange(newCursor, newCursor);
    }

    // Only one message shows at a time — an over-limit attempt outranks the
    // invalid-characters one (the length cap is what actually stopped the
    // keystroke), and both outrank the debounced min message (already
    // hidden below while typing).
    if (maxLengthError) {
      if (maxAttempt) maxLengthError.hidden = false;
      // An invalid-char attempt takes the slot over; otherwise the max
      // message stays while the value still sits at the limit and resolves
      // once the customer deletes below it.
      else if (invalidAttempt || textInput.value.length < max) maxLengthError.hidden = true;
    }
    if (invalidCharsError) invalidCharsError.hidden = !(invalidAttempt && !maxAttempt);

    if (previewText) {
      const { value } = textInput;
      previewText.textContent = value.length > 20 ? `${value.slice(0, 20)}...` : value;
    }
    if (emptyTextError) emptyTextError.hidden = true;
    this.#syncLengthValidity();

    // Hide the min error while the customer is still typing; it only
    // (re)surfaces once typing pauses, via the debounced validation below.
    // On an attempt keystroke (invalid char / over the limit), don't
    // schedule that check at all: the attempt message keeps the slot for
    // this pause instead of being swapped for the min message half a
    // second later. The min check resumes on the next clean input (and
    // still runs on blur and on a blocked submit).
    if (minLengthError) minLengthError.hidden = true;
    clearTimeout(this.#lengthValidationTimer);
    if (!invalidAttempt && !maxAttempt) {
      this.#lengthValidationTimer = setTimeout(() => this.#validateTextLength(), 500);
    }
  }

  // Gates native form submission on the JS min-length rule: while the value
  // is too short, a customError on the input makes every submit path that
  // runs constraint validation (add-to-cart click, requestSubmit) fire
  // `invalid` — caught by #handleInvalid — instead of `submit`. The message
  // string itself is never shown; the themed inline errors are. Too-long
  // values can't occur (handleTextInput truncates), and a missing/blank
  // data-min-chars parses to 0, so no limit ever blocks add-to-cart when
  // the metafields aren't set.
  #syncLengthValidity() {
    const { textInput } = this.refs;
    if (!textInput) return;
    const length = textInput.value.length;
    const min = parseInt(textInput.dataset.minChars || '', 10) || 0;
    textInput.setCustomValidity(length > 0 && length < min ? 'too_short' : '');
  }

  handleTextBlur() {
    const { maxLengthError } = this.refs;
    // The value is already capped at the max, so the attempt message is
    // only relevant while the customer is still in the field.
    if (maxLengthError) maxLengthError.hidden = true;
    this.#trimTextInput();
    // Leaving the field ends typing — validate right away instead of
    // waiting out the debounce.
    clearTimeout(this.#lengthValidationTimer);
    this.#validateTextLength();
  }

  // Strips leading/trailing spaces — spaces are only meaningful between
  // characters, so padding at the edges is never intentional content. If
  // that leaves nothing behind (a spaces-only value), the field is now
  // genuinely empty and falls through to the native `required` check
  // (#handleInvalid) instead of being treated as valid text.
  #trimTextInput() {
    const { textInput, previewText } = this.refs;
    if (!textInput) return;
    const trimmed = textInput.value.trim();
    if (trimmed === textInput.value) return;
    textInput.value = trimmed;
    if (previewText) {
      previewText.textContent = trimmed.length > 20 ? `${trimmed.slice(0, 20)}...` : trimmed;
    }
    this.#syncLengthValidity();
  }

  #pushGaEvent() {
    const dataLayer = /** @type {any} */ (window).dataLayer;
    if (Array.isArray(dataLayer)) {
      dataLayer.push({ event: 'add_to_cart_diff', add_to_cart_label: 'PDP - Add custom item to bag' });
    }
  }

  /** @param {boolean} open */
  #setOpen(open) {
    this.dataset.open = open ? 'true' : 'false';
    if (this.refs.panel) this.refs.panel.hidden = !open;
    this.refs.toggleButton?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // Keeps the checkbox/toggle in sync with the currently selected variant's
  // stock status — an out-of-stock variant shouldn't let the shopper open
  // the accordion at all. If engraving was already active when the
  // shopper switches to an unavailable variant, it's torn down the same
  // way a successful cart-add resets it.
  /** @param {boolean} available */
  #setAvailability(available) {
    this.dataset.available = available ? 'true' : 'false';

    const { engravingCheckbox, toggleButton } = this.refs;
    if (engravingCheckbox) engravingCheckbox.disabled = !available;
    if (toggleButton) toggleButton.disabled = !available;

    if (!available && this.dataset.active === 'true') {
      if (engravingCheckbox) engravingCheckbox.checked = false;
      this.#setOpen(false);
      this.#clearInputs();
      this.#applyState(false, { skipCtaText: true });
    } else if (!available) {
      this.#setOpen(false);
    }
  }

  #clearInputs() {
    const {
      textInput,
      previewText,
      minLengthError,
      maxLengthError,
      invalidCharsError,
      emptyTextError,
      styleInput,
      disclaimerCheckbox,
      disclaimerError,
    } = this.refs;

    clearTimeout(this.#lengthValidationTimer);
    if (textInput) {
      textInput.value = '';
      this.#syncLengthValidity();
    }
    if (previewText) {
      previewText.textContent = '';
      previewText.style.fontFamily = '';
    }
    if (minLengthError) minLengthError.hidden = true;
    if (maxLengthError) maxLengthError.hidden = true;
    if (invalidCharsError) invalidCharsError.hidden = true;
    if (emptyTextError) emptyTextError.hidden = true;
    if (disclaimerCheckbox) disclaimerCheckbox.checked = false;
    if (disclaimerError) disclaimerError.hidden = true;

    const options = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.product-engraving__style-option'))
    );
    options.forEach((option, index) => {
      const isFirst = index === 0;
      option.dataset.selected = isFirst ? 'true' : 'false';
      option.setAttribute('aria-pressed', isFirst ? 'true' : 'false');
    });
    if (styleInput && options[0]) styleInput.value = options[0].textContent?.trim() || '';
  }

  /**
   * @param {boolean} active
   * @param {{ skipCtaText?: boolean }} [options] - Pass `skipCtaText: true` when
   *   the CTA text is about to be owned by something else (e.g. the variant
   *   just went out of stock, and product-form.js's own variant:update
   *   handler morphs the button to "Sold out" — writing "Add to bag" here
   *   would stomp on that).
   */
  #applyState(active, { skipCtaText = false } = {}) {
    this.dataset.active = active ? 'true' : 'false';
    this.#setFieldsEnabled(active);
    if (!skipCtaText) this.#updateCtaText(active);
    this.#updateDeliveryDates(active);

    if (active) {
      const form = this.#getProductForm();
      if (form) this.#applyEngravingCartProperties(form);
    } else {
      this.#removeEngravingCartProperties();
    }

    // Lets other scripts react to engraving being toggled — cause.js
    // recomputes the charity impact message with the engraving cost
    // factored in.
    document.dispatchEvent(
      new CustomEvent('engraving:change', {
        detail: { active, productId: this.dataset.productId },
      })
    );
  }

  // The text/style/disclaimer fields are only associated with the product
  // form while engraving is active — a disabled form field is excluded from
  // submission, so a normal (non-engraved) add-to-cart never carries a
  // leftover "Engraving font"/disclaimer property from this UI.
  /** @param {boolean} enabled */
  #setFieldsEnabled(enabled) {
    const { textInput, styleInput, disclaimerCheckbox } = this.refs;
    [textInput, styleInput, disclaimerCheckbox].forEach((field) => {
      if (!field) return;
      field.disabled = !enabled;
      const wrapper = field.closest('.checkbox');
      if (wrapper) wrapper.classList.toggle('checkbox--disabled', !enabled);
    });
  }

  /** @param {boolean} active */
  #updateCtaText(active) {
    const text = active ? this.dataset.addCustomItemText : this.dataset.addToBagText;
    if (text) {
      this.#getAddToCartTextElements().forEach((element) => {
        element.textContent = text;
      });
    }
    this.#updateOverlayGaLabel(active);
  }

  // The mobile sticky overlay button (snippets/mobile-atc-overlay.liquid)
  // carries a static `data-ga-label="PDP - Add to cart - overlay"` attribute
  // — nothing in this codebase reads it, but the naming convention suggests
  // it may be a GTM click-attribute trigger reading the DOM directly rather
  // than the dataLayer.push above, so keep it in sync too.
  /** @param {boolean} active */
  #updateOverlayGaLabel(active) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    document
      .querySelectorAll(`[data-section-id="${CSS.escape(sectionId)}"] .mobile-atc-overlay__button`)
      .forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        if (active) {
          if (button.dataset.gaLabelOriginal === undefined) {
            button.dataset.gaLabelOriginal = button.getAttribute('data-ga-label') || '';
          }
          button.setAttribute('data-ga-label', 'PDP - Add custom item to bag');
        } else if (button.dataset.gaLabelOriginal !== undefined) {
          button.setAttribute('data-ga-label', button.dataset.gaLabelOriginal);
        }
      });
  }

  /** @returns {HTMLElement[]} */
  #getAddToCartTextElements() {
    const formId = this.dataset.productFormId;
    const sectionId = this.dataset.sectionId;
    /** @type {Set<HTMLElement>} */
    const elements = new Set();

    if (formId) {
      document
        .querySelectorAll(`#${CSS.escape(formId)} .add-to-cart-text__content`)
        .forEach((element) => elements.add(/** @type {HTMLElement} */ (element)));
    }
    if (sectionId) {
      // .add-to-cart-text__content covers the main inline button; the
      // mobile sticky overlay (snippets/mobile-atc-overlay.liquid) renders
      // its own separate button with its own text span, so it needs its
      // own selector to stay in sync when engraving is active.
      document
        .querySelectorAll(
          `[data-section-id="${CSS.escape(sectionId)}"] .add-to-cart-text__content,` +
            `[data-section-id="${CSS.escape(sectionId)}"] .mobile-atc-overlay__button-text`
        )
        .forEach((element) => elements.add(/** @type {HTMLElement} */ (element)));
    }

    return Array.from(elements);
  }

  /** @param {boolean} active */
  #updateDeliveryDates(active) {
    const { deliveryMessage } = this.refs;
    const olivelaDelivery = /** @type {any} */ (window).OlivelaDelivery;
    // The PDP can render more than one estimated-dates block (standard,
    // express, international shipping options inside "Delivery & Returns",
    // plus a separate top-of-page estimated-dates block) — each is its own
    // .estimated-dates-placeholder with its own lead-time/carrier settings,
    // so every one of them needs updating independently.
    const placeholders = /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('.estimated-dates-placeholder'))
    );

    const formId = this.dataset.productFormId;
    const form = /** @type {HTMLFormElement | null} */ (formId ? document.getElementById(formId) : null);
    const estimateInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('[name="properties[_estimate]"]')
    );
    const shipInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('[name="properties[_expected-ship]"]')
    );

    if (!this.#cachedOriginals) {
      this.#originalEstimate = estimateInput?.value ?? null;
      this.#originalExpectedShip = shipInput?.value ?? null;
      if (form) {
        const existingEngravingLeadTime = /** @type {HTMLInputElement | null} */ (
          form.querySelector('[name="properties[_engraving_lead_time]"]')
        );
        this.#originalEngravingLeadTime = existingEngravingLeadTime?.value ?? null;
        this.#engravingLeadTimeInputCreated = !existingEngravingLeadTime;
      }
      placeholders.forEach((placeholder) => {
        this.#originalPlaceholderHtml.set(placeholder, placeholder.innerHTML);
      });
      this.#cachedOriginals = true;
    }

    if (!active) {
      if (estimateInput) estimateInput.value = this.#originalEstimate || '';
      if (shipInput) shipInput.value = this.#originalExpectedShip || '';
      if (form) {
        const engravingLeadTimeInput = /** @type {HTMLInputElement | null} */ (
          form.querySelector('[name="properties[_engraving_lead_time]"]')
        );
        if (engravingLeadTimeInput) {
          if (this.#engravingLeadTimeInputCreated) engravingLeadTimeInput.remove();
          else engravingLeadTimeInput.value = this.#originalEngravingLeadTime || '';
        }
      }
      placeholders.forEach((placeholder) => {
        const originalHtml = this.#originalPlaceholderHtml.get(placeholder);
        if (originalHtml !== undefined) placeholder.innerHTML = originalHtml;
      });
      if (deliveryMessage) deliveryMessage.hidden = true;
      return;
    }

    if (!olivelaDelivery) return;

    const engravingLeadDays = parseInt(this.dataset.leadDays || '', 10) || 0;

    // Persist the engraving lead time (from the product's
    // lead_shipping_time_engraving_ metafield) as its own cart-line
    // property, kept separate from the shared `lead_time` property so it
    // never collides with the base product's own lead time. The theme's
    // cart recalculation (assets/estimated-dates.js) adds this on top of
    // `lead_time` on every future recalculation.
    if (form) {
      let engravingLeadTimeInput = /** @type {HTMLInputElement | null} */ (
        form.querySelector('[name="properties[_engraving_lead_time]"]')
      );
      if (!engravingLeadTimeInput) {
        engravingLeadTimeInput = document.createElement('input');
        engravingLeadTimeInput.type = 'hidden';
        engravingLeadTimeInput.name = 'properties[_engraving_lead_time]';
        form.appendChild(engravingLeadTimeInput);
      }
      engravingLeadTimeInput.value = String(engravingLeadDays);
    }

    // The visible "New estimated delivery" message inside the accordion
    // uses the first (standard shipping) placeholder's window, matching
    // what the hidden cart-line properties below also use.
    let primaryWindowText = '';

    placeholders.forEach((placeholder, index) => {
      const tags = (placeholder.getAttribute('data-product-tags') || '').split(',');
      const carrierMin = parseInt(placeholder.getAttribute('data-carrier-min') || '', 10) || 3;
      const carrierMax = parseInt(placeholder.getAttribute('data-carrier-max') || '', 10) || 4;
      const baseLeadTime = parseInt(placeholder.getAttribute('data-lead-time') || '', 10) || 0;

      const props = olivelaDelivery.computeDeliveryProps(tags, baseLeadTime + engravingLeadDays, carrierMin, carrierMax);
      if (!props['_estimate']) return;

      const windowText = props['_estimate'].replace(/^Estimated delivery\s*/i, '');
      placeholder.innerHTML = `<p>Estimated delivery ${windowText}</p>`;

      if (index === 0) {
        primaryWindowText = windowText;
        if (estimateInput) estimateInput.value = props['_estimate'];
        if (shipInput && props['_expected-ship']) shipInput.value = props['_expected-ship'];
      }
    });

    if (deliveryMessage && engravingLeadDays > 0 && primaryWindowText) {
      const template = this.dataset.newDeliveryTemplate || '';
      deliveryMessage.innerHTML = template.replace('__DELIVERY_WINDOW__', primaryWindowText);
      deliveryMessage.hidden = false;
    }
  }
}

customElements.define('product-engraving-component', ProductEngravingComponent);
